// src/components/ImageUploader.jsx
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { X, UploadCloud, FileText, Paperclip, Film, Music, FileArchive, File as FileIcon, Image as ImageIcon } from 'lucide-react'; // Added more icons
import { cn } from '../lib/utils';
import { Label } from './ui/label';
const MAX_FILE_SIZE_MB_DEFAULT = 5;


const getFileExtension = (filename) => {
  if (!filename || typeof filename !== 'string') return '';
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
};

const isImageFile = (fileOrUrlOrFileData) => {
  let typeOrExt;
  let filename;

  if (fileOrUrlOrFileData instanceof File) {
    typeOrExt = fileOrUrlOrFileData.type;
    return typeOrExt.startsWith('image/');
  }
  if (typeof fileOrUrlOrFileData === 'string') { // Assumed to be a URL
    filename = fileOrUrlOrFileData;
  } else if (fileOrUrlOrFileData && typeof fileOrUrlOrFileData.previewUrl === 'string') { // fileData object
    filename = fileOrUrlOrFileData.previewUrl;
    if (fileOrUrlOrFileData.file instanceof File && fileOrUrlOrFileData.file.type.startsWith('image/')) {
        return true; // If original file object is image
    }
  } else if (fileOrUrlOrFileData && fileOrUrlOrFileData.file instanceof File) { // fileData object with only file
     typeOrExt = fileOrUrlOrFileData.file.type;
     return typeOrExt.startsWith('image/');
  }


  if (filename) {
    typeOrExt = getFileExtension(filename);
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'avif'].includes(typeOrExt);
  }
  return false;
};

const FilePreviewIcon = ({ filename }) => {
    const extension = getFileExtension(filename);
    let specificIcon = <FileIcon className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />;

    switch (extension) {
        case 'pdf':
            specificIcon = <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" />;
            break;
        case 'doc':
        case 'docx':
            specificIcon = <Paperclip className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />;
            break;
        case 'zip':
        case 'rar':
        case 'tar':
        case 'gz':
            specificIcon = <FileArchive className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-600" />;
            break;
        case 'mp4':
        case 'mov':
        case 'avi':
        case 'wmv':
             specificIcon = <Film className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600" />;
             break;
        case 'mp3':
        case 'wav':
        case 'ogg':
            specificIcon = <Music className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />;
            break;
        default:
            break;
    }
    return specificIcon;
};


const ImageUploaderComponent = ({
  label,
  name,
  multiple = false,
  maxFiles = multiple ? 5 : 1,
  onChange,
  maxSizeMB = MAX_FILE_SIZE_MB_DEFAULT,
  note,
  initialUrls = [],
  fileType = "image/*", // Can be more specific like "image/jpeg, image/png, application/pdf"
  className,
  onBrowse,
}) => {
  const [filesData, setFilesData] = useState([]);
  const fileInputRef = useRef(null);
  //console.log("S3 link:",process.env.REACT_APP_S3_ASSETS_FETCH_URL)

  useEffect(() => {
    // This effect syncs `initialUrls` to the `filesData` state,
    // primarily for initializing presets or if presets change externally.
    // It should avoid interfering with user's local selections.
    setFilesData(currentLocalFilesData => {
      const localUserSelectedFiles = currentLocalFilesData.filter(fd => fd.file !== null);

      const newPresetObjects = (Array.isArray(initialUrls) ? initialUrls : [])
        .filter(url => typeof url === 'string' && url.trim() !== '')
        .map((url, i) => ({
          file: null,
          previewUrl: url,
          // STABLE ID for presets:
          id: `preset-${url.substring(Math.max(0, url.length - 20))}-${i}`,
          isPreset: true,
        }));

      if (localUserSelectedFiles.length > 0 && !multiple) {
        // In single file mode, if user has selected a file, don't let initialUrls overwrite it.
        // Parent should clear initialUrls if the local file takes precedence.
        // Or, if an initialUrl is set, it implies the local file should be cleared.
        // For simplicity here, if local files exist in single mode, we might keep them.
        // However, the provided code would replace on new selection, this effect handles initial state.
         const currentPresetIdsInState = currentLocalFilesData.filter(fd => fd.isPreset).map(fd => fd.id).sort().join(',');
         const newPresetIdsFromProp = newPresetObjects.map(fd => fd.id).sort().join(',');
         if(newPresetIdsFromProp !== currentPresetIdsInState && newPresetObjects.length > 0){
             // If presets change and there's a local file in single mode, presets take precedence
             localUserSelectedFiles.forEach(f => {if (f.previewUrl.startsWith('blob:')) URL.revokeObjectURL(f.previewUrl);});
             return newPresetObjects.slice(0,1); // Take only one preset for single mode
         }
         return currentLocalFilesData;
      }
      
      if (localUserSelectedFiles.length > 0 && multiple) {
          // In multiple mode, merge existing local files with new presets if presets changed
          const currentPresetIdsInState = currentLocalFilesData.filter(fd => fd.isPreset).map(fd => fd.id).sort().join(',');
          const newPresetIdsFromProp = newPresetObjects.map(fd => fd.id).sort().join(',');

          if (newPresetIdsFromProp !== currentPresetIdsInState) {
              // Filter out old presets that are not in new initialUrls
              const stillValidPresets = currentLocalFilesData.filter(fd => fd.isPreset && initialUrls.includes(fd.previewUrl));
              const combined = [...stillValidPresets, ...localUserSelectedFiles];
              // Add new presets that weren't there before
              newPresetObjects.forEach(np => {
                  if (!combined.find(c => c.id === np.id)) combined.unshift(np); // Add new presets at beginning
              });
              return combined.slice(0, maxFiles); // Ensure maxFiles is respected
          }
          return currentLocalFilesData; // No change to presets needed
      }

      // No local files, so just use the new presets.
      // Check if new presets are different from current (likely empty) presets.
      const currentPresetIdsInState = currentLocalFilesData.filter(fd => fd.isPreset).map(fd => fd.id).sort().join(',');
      const newPresetIdsFromProp = newPresetObjects.map(fd => fd.id).sort().join(',');

      if (newPresetIdsFromProp !== currentPresetIdsInState) {
        return newPresetObjects.slice(0, maxFiles);
      }
      return currentLocalFilesData; // No change
    });
  }, [initialUrls, multiple, maxFiles]); // Removed filesData from deps to break loop. Logic relies on functional update.


  useEffect(() => {
    const currentFilesForCleanup = filesData;
    return () => {
      currentFilesForCleanup.forEach(f => {
        if (f.previewUrl && f.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(f.previewUrl);
        }
      });
    };
  }, [filesData]);

  const handleFileSelection = useCallback((e) => {
    const selectedHtmlFiles = Array.from(e.target.files);
    const newLocalFileDataObjects = [];

    for (let htmlFile of selectedHtmlFiles) {
      if (htmlFile.size > maxSizeMB * 1024 * 1024) {
        alert(`File "${htmlFile.name}" exceeds ${maxSizeMB}MB.`);
        continue;
      }
      newLocalFileDataObjects.push({
        file: htmlFile,
        previewUrl: URL.createObjectURL(htmlFile),
        id: `${htmlFile.name}-${htmlFile.lastModified}-${htmlFile.size}-${Math.random().toString(36).substring(2, 5)}`,
        isPreset: false,
      });
    }

    if (newLocalFileDataObjects.length === 0 && selectedHtmlFiles.length > 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    
    setFilesData(prevFilesData => {
      let newCombinedList;
      if (multiple) {
        const existingPresets = prevFilesData.filter(fd => fd.isPreset);
        let existingUserFiles = prevFilesData.filter(fd => fd.file && !fd.isPreset);

        // Add new files, ensuring no ID collisions with current user files
        newLocalFileDataObjects.forEach(newFile => {
          if (!existingUserFiles.find(existing => existing.id === newFile.id)) {
            existingUserFiles.push(newFile);
          }
        });
        
        const slotsForUserFiles = Math.max(0, maxFiles - existingPresets.length);
        if (existingUserFiles.length > slotsForUserFiles) {
          existingUserFiles = existingUserFiles.slice(existingUserFiles.length - slotsForUserFiles); // Keep newest
        }
        newCombinedList = [...existingPresets, ...existingUserFiles];

      } else { // Single file mode
        // Revoke URL for any previous local file
        prevFilesData.filter(fd => fd.file && !fd.isPreset).forEach(fd => {
            if (fd.previewUrl.startsWith('blob:')) URL.revokeObjectURL(fd.previewUrl);
        });
        if (newLocalFileDataObjects.length > 0) {
          newCombinedList = newLocalFileDataObjects.slice(0, 1); // Only the new file
        } else {
          // If no new file selected, keep presets (already handled by useEffect for initialUrls)
          // or clear if desired (current behaviour: keep presets if no new file selected)
          newCombinedList = prevFilesData.filter(fd => fd.isPreset).slice(0,1); 
        }
      }
      
      // Final trim to maxFiles overall
      if (newCombinedList.length > maxFiles) {
        newCombinedList = newCombinedList.slice(newCombinedList.length - maxFiles);
      }

      const filesToPassOnChange = newCombinedList.map(fd => fd.file).filter(f => f !== null);
      if (onChange) {
        onChange(multiple ? filesToPassOnChange : (filesToPassOnChange[0] || null));
      }
      return newCombinedList;
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [multiple, maxFiles, maxSizeMB, onChange]); // Removed initialUrls and filesData (using functional update for filesData)

  const handleRemove = useCallback((idToRemove) => {
    setFilesData(prevFilesData => {
      const fileToRemove = prevFilesData.find(f => f.id === idToRemove);
      if (fileToRemove && fileToRemove.previewUrl && fileToRemove.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      const updatedFilesData = prevFilesData.filter(f => f.id !== idToRemove);
      
      const filesToPassOnChange = updatedFilesData.map(fd => fd.file).filter(f => f !== null);
      if (onChange) {
          onChange(multiple ? filesToPassOnChange : (filesToPassOnChange[0] || null));
      }
      return updatedFilesData;
    });
  }, [onChange, multiple]); // Removed filesData

  const handleDragStart = useCallback((e, index) => { e.dataTransfer.setData('dragIndex', index.toString()); }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'), 10);
    if (isNaN(dragIndex) || dragIndex === dropIndex) return;

    setFilesData(prevFilesData => {
      const newOrder = [...prevFilesData];
      const [movedItem] = newOrder.splice(dragIndex, 1);
      newOrder.splice(dropIndex, 0, movedItem);
      
      const filesToPassOnChange = newOrder.map(fd => fd.file).filter(f => f !== null);
      if (onChange) {
          onChange(multiple ? filesToPassOnChange : (filesToPassOnChange[0] || null));
      }
      return newOrder;
    });
  }, [onChange, multiple]); // Removed filesData

  const triggerFileInput = useCallback(() => { fileInputRef.current?.click(); }, []);

  return (
    <div className={cn("space-y-3 w-full", className)}>
      {label && <Label className="text-sm font-medium text-foreground">{label}</Label>}
      <div
        className="flex flex-col items-center justify-center w-full px-4 py-6 border-2 border-border border-dashed rounded-md cursor-pointer hover:border-primary transition-colors bg-card"
        onClick={triggerFileInput}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation();
          if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelection({ target: { files: e.dataTransfer.files } });
            e.dataTransfer.clearData();
          }
        }}
      >
        <UploadCloud className="mx-auto h-10 w-10 text-muted-foreground" />
        <div className="flex items-center gap-3 mb-2">
            <Button 
                type="button" 
                variant="secondary" 
                size="sm" 
                onClick={triggerFileInput}
            >
                Select File
            </Button>

            {onBrowse && (
                <>
                    <span className="text-xs text-muted-foreground">or</span>
                    <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); onBrowse(); }}
                        className="gap-2"
                    >
                        <ImageIcon className="w-4 h-4"/> 
                        Browse Library
                    </Button>
                </>
            )}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          <span className="font-semibold text-primary">Click to upload</span> or drag and drop
        </p>
        {fileType && <p className="text-xs text-muted-foreground">{fileType.replace('image/*', 'Images').replace(/application\/pdf/g, 'PDFs')}</p>}
        <p className="text-xs text-muted-foreground">Max {maxSizeMB}MB per file. {multiple && `Up to ${maxFiles} files.`}</p>
      </div>
      <input ref={fileInputRef} type="file" name={name} multiple={multiple} accept={fileType} onChange={handleFileSelection} className="hidden"/>
      {note && <p className="text-xs text-muted-foreground mt-1">{note}</p>}

      {filesData.length > 0 && (
        <ul className={`grid gap-3 mt-3 ${multiple ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5' : 'grid-cols-1 max-w-[150px] sm:max-w-[200px]'}`}>
          {filesData.map((fileData, index) => {
            const isImage = isImageFile(fileData); // Pass the whole fileData object
            const filename = fileData.file?.name || (typeof fileData.previewUrl === 'string' ? fileData.previewUrl.substring(fileData.previewUrl.lastIndexOf('/') + 1).split('?')[0] : "file"); // Get basename from URL
            
            return (
              <li
                key={fileData.id}
                draggable={multiple}
                onDragStart={(e) => handleDragStart(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragOver={(e) => e.preventDefault()}
                className="relative border border-border rounded-md overflow-hidden shadow group aspect-square bg-muted flex flex-col items-center justify-center p-1"
                title={multiple ? "Drag to reorder" : filename}
              >
                {isImage ? (
                  <img src={fileData.isPreset? `${process.env.REACT_APP_S3_ASSETS_FETCH_URL}/${fileData.previewUrl}` : fileData.previewUrl} alt={`Preview of ${filename}`}
                      className="object-cover w-full h-full"/>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center w-full h-full p-2">
                    <FilePreviewIcon filename={filename} />
                    <p className="text-xs text-foreground mt-1 break-all line-clamp-2" title={filename}>
                      {filename}
                    </p>
                  </div>
                )}
                <Button
                  type="button" variant="destructive" size="icon"
                  onClick={() => handleRemove(fileData.id)}
                  className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity bg-destructive/80 hover:bg-destructive"
                  aria-label="Remove file"
                >
                  <X className="h-3.5 w-3.5" /> 
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
export default React.memo(ImageUploaderComponent);